import { useCallback, useMemo } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { readContract, waitForTransactionReceipt } from '@wagmi/core';
import { Address } from 'viem';
import { useReadContract, useReadContracts } from 'wagmi';

import { useAuth } from '@contexts/common/AuthorizationContext';

import { config } from '@tools/wagmi';

import { mapWagmiError } from '@helpers/common/error.helpers';

import { useWriteContractExtraGas } from '@utils/contract.utils';

import showToast from '@components/common/Toast';

import { AvailableChainIdType } from '@constants/common/chain.constants';
import { SuccessMessages } from '@constants/common/messages.constants';
import { ToastType } from '@constants/common/toast.constants';
import { MARKETPLACE } from '@constants/fusion/contracts.constants';

import abi from '@abi/fusion/marketplace';

import { OrderDto } from '@dto/fusion/fusion.dto';

export const useGetActiveOrdersIdsCount = (chainId: AvailableChainIdType) => {
  return useReadContract({
    address: MARKETPLACE,
    abi,
    functionName: 'activeOrderIdsCount',
    chainId,
    query: {
      enabled: !!chainId,
    },
  });
};

export const useGetActiveOrdersIds = (chainId: AvailableChainIdType) => {
  const {
    data: ordersIdCount,
    refetch: refetchOrdersIdCount,
    isPending,
  } = useGetActiveOrdersIdsCount(chainId);

  const ordersIndexesArray = useMemo<bigint[]>(() => {
    if (!ordersIdCount) return [];

    return Array.from({ length: Number(ordersIdCount) }, (_, i) => BigInt(i));
  }, [ordersIdCount]);

  const contracts = useMemo(() => {
    if (!ordersIndexesArray.length || !chainId) return [];

    return ordersIndexesArray.map((index) => ({
      address: MARKETPLACE,
      abi,
      functionName: 'activeOrderIds' as const,
      args: [index],
      chainId,
    }));
  }, [ordersIndexesArray, chainId]);

  const { data, ...rest } = useReadContracts({
    contracts,
    query: {
      enabled: !!chainId && !!ordersIndexesArray.length,
    },
    allowFailure: false,
  });

  return {
    ...rest,
    data,
    isPending,
    refetch: refetchOrdersIdCount,
  };
};

export const useGetUserOrdersIdsCount = (chainId: AvailableChainIdType) => {
  const { account } = useAuth();

  return useReadContract({
    address: MARKETPLACE,
    abi,
    functionName: 'ordersUserToIdsCount',
    args: [account as Address],
    chainId,
    query: {
      enabled: !!chainId && !!account,
    },
  });
};

export const useGetUserOrdersIds = (chainId: AvailableChainIdType) => {
  const { account } = useAuth();
  const {
    data: ordersIdCount,
    refetch: refetchOrdersIdCount,
    isPending,
  } = useGetUserOrdersIdsCount(chainId);

  const ordersIndexesArray = useMemo(() => {
    if (!ordersIdCount) return [];

    return Array.from({ length: Number(ordersIdCount) }, (_, i) => BigInt(i));
  }, [ordersIdCount]);

  const contracts = useMemo(() => {
    if (!ordersIndexesArray.length || !chainId || !account) return [];

    return ordersIndexesArray.map((index) => ({
      address: MARKETPLACE,
      abi,
      functionName: 'ordersUserToIds' as const,
      args: [index, account],
      chainId,
    }));
  }, [ordersIndexesArray, chainId, account]);

  const { data, ...rest } = useReadContracts({
    contracts,
    query: {
      enabled: !!chainId && !!ordersIndexesArray.length,
    },
    allowFailure: false,
  });

  return {
    ...rest,
    data,
    isPending,
    refetch: refetchOrdersIdCount,
  };
};

type GetOrdersParamType = {
  chainId: AvailableChainIdType;
  orderIds?: bigint[];
};

export const useGetOrders = ({ chainId, orderIds }: GetOrdersParamType) => {
  const contracts = useMemo(() => {
    if (!orderIds?.length || !chainId) return [];

    return orderIds.map((id) => ({
      address: MARKETPLACE,
      abi,
      functionName: 'orders' as const,
      args: [id],
      chainId,
    }));
  }, [orderIds, chainId]);

  const { data, ...rest } = useReadContracts({
    contracts,
    query: {
      enabled: !!chainId && !!orderIds?.length,
      select: (orders): OrderDto[] => {
        if (!orderIds || orders.length !== orderIds.length) {
          console.warn('useGetOrders: orderIds length mismatch with orders');
          return [];
        }

        return orders.map((order, index) => {
          const [seller, amountSale, pricePerOne, timestamp, status] = order;
          const id = orderIds[index];
          return {
            orderId: Number(id),
            seller,
            amount: String(amountSale),
            pricePerOne: String(pricePerOne),
            timestamp: String(timestamp),
            status,
          };
        });
      },
    },
    allowFailure: false,
  });

  return {
    ...rest,
    data,
  };
};

type GetOrderParamType = {
  chainId: AvailableChainIdType;
  orderId: number;
};

export const useGetOrder = ({ chainId, orderId }: GetOrderParamType) => {
  return useReadContract({
    address: MARKETPLACE,
    abi,
    functionName: 'orders' as const,
    args: [BigInt(orderId)],
    chainId,
    query: {
      enabled: !!chainId && !!orderId,
      select: (order): OrderDto => {
        const [seller, amountSale, pricePerOne, timestamp, status] = order;
        return {
          orderId,
          seller,
          amount: String(amountSale),
          pricePerOne: String(pricePerOne),
          timestamp: String(timestamp),
          status,
        };
      },
    },
  });
};

/* ------------------------------------------------ ACTIONS ------------------------------------------------------------ */

export const useCreateOrder = () => {
  const { chainId, account } = useAuth();
  const queryClient = useQueryClient();

  const { writeContractAsync, ...rest } = useWriteContractExtraGas({
    config,
    mutation: {
      onSuccess: async (data) => {
        const transactionReceipt = await waitForTransactionReceipt(config, {
          hash: data,
        });

        await queryClient.invalidateQueries();

        showToast(
          {
            title: SuccessMessages.Created,
            withLink: {
              hash: transactionReceipt.transactionHash,
              chainId: chainId as AvailableChainIdType,
            },
          },
          ToastType.Success,
        );
      },
      onError: (e) => {
        console.log('useCreateOrder:', e);
        showToast({ title: mapWagmiError(e) }, ToastType.Error);
      },
    },
  });

  const onWriteContract = useCallback(
    async ({ amount, price }: { amount: bigint; price: bigint }) => {
      await writeContractAsync({
        abi,
        address: MARKETPLACE,
        functionName: 'createOrder',
        chainId,
        args: [amount, price],
        account,
      });
    },
    [chainId, writeContractAsync, account],
  );

  return {
    writeContractAsync: onWriteContract,
    ...rest,
  };
};

export const useBuyOrder = () => {
  const { chainId, account } = useAuth();
  const queryClient = useQueryClient();

  const { writeContractAsync, ...rest } = useWriteContractExtraGas({
    config,
    mutation: {
      onSuccess: async (data) => {
        const transactionReceipt = await waitForTransactionReceipt(config, {
          hash: data,
        });

        await queryClient.invalidateQueries();

        showToast(
          {
            title: SuccessMessages.Bought,
            withLink: {
              hash: transactionReceipt.transactionHash,
              chainId: chainId as AvailableChainIdType,
            },
          },
          ToastType.Success,
        );
      },
      onError: (e) => {
        console.log('useBuyOrder:', e);
        showToast({ title: mapWagmiError(e) }, ToastType.Error);
      },
    },
  });

  const onWriteContract = useCallback(
    async ({
      orderId,
      amount,
      pricePerOne,
    }: {
      orderId: bigint;
      amount: bigint;
      pricePerOne: bigint;
    }) => {
      const calculateFee = await readContract(config, {
        abi,
        address: MARKETPLACE,
        functionName: 'calculateFee',
        args: [pricePerOne, amount],
      });
      if (!calculateFee) {
        const error = new Error('Failed to calculate fee');
        showToast({ title: mapWagmiError(error) }, ToastType.Error);
        return;
      }

      await writeContractAsync({
        abi,
        address: MARKETPLACE,
        functionName: 'buyOrder',
        chainId,
        args: [orderId, amount],
        value: calculateFee[2],
        account,
      });
    },
    [chainId, writeContractAsync, account],
  );

  return {
    writeContractAsync: onWriteContract,
    ...rest,
  };
};

export const useCancelOrder = () => {
  const { chainId, account } = useAuth();
  const queryClient = useQueryClient();

  const { writeContractAsync, ...rest } = useWriteContractExtraGas({
    config,
    mutation: {
      onSuccess: async (data) => {
        const transactionReceipt = await waitForTransactionReceipt(config, {
          hash: data,
        });

        await queryClient.invalidateQueries();

        showToast(
          {
            title: SuccessMessages.Canceled,
            withLink: {
              hash: transactionReceipt.transactionHash,
              chainId: chainId as AvailableChainIdType,
            },
          },
          ToastType.Success,
        );
      },
      onError: (e) => {
        console.log('useCancelOrder:', e);
        showToast({ title: mapWagmiError(e) }, ToastType.Error);
      },
    },
  });

  const onWriteContract = useCallback(
    async ({ orderId }: { orderId: bigint }) => {
      await writeContractAsync({
        abi,
        address: MARKETPLACE,
        functionName: 'cancelOrder',
        chainId,
        args: [orderId],
        account,
      });
    },
    [chainId, writeContractAsync, account],
  );

  return {
    writeContractAsync: onWriteContract,
    ...rest,
  };
};

export const useCalculateFee = ({
  chainId,
  price,
  amount,
}: {
  chainId: AvailableChainIdType;
  price: bigint;
  amount: bigint;
}) => {
  return useReadContract({
    address: MARKETPLACE,
    abi,
    functionName: 'calculateFee',
    args: [price, amount],
    chainId,
    query: {
      enabled: !!chainId,
      select: (res) => ({
        buyerFeeAmount: String(res[0]),
        sellerFeeAmount: String(res[1]),
        totalAmount: String(res[2]),
      }),
    },
  });
};
